import { useQueryClient, UseMutationOptions } from 'react-query';
import produce from 'immer';

import {
  CreateClientStickerMutation,
  CreateClientStickerMutationVariables,
  S3Object,
  BootstrapQuery,
} from 'api';
import { useBootstrapQueryKey, useNotify } from 'hooks';
import { STICKER_SIZE } from 'features/admin/constants';

type Mutation = CreateClientStickerMutation;
type MutationVariables = CreateClientStickerMutationVariables;
type QueryData = BootstrapQuery;

const useCreateSticker = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const bootstrapQueryKey = useBootstrapQueryKey();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: ({ data }) => {
      const { image } = data;

      const sticker: S3Object = {
        bucket: image.bucket,
        height: STICKER_SIZE,
        width: STICKER_SIZE,
        key: 'loaders/sticker.gif?format=auto',
        region: image.region,
      };

      const baseState = queryClient.getQueryData<QueryData>(bootstrapQueryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.bootstrap.client.stickers.unshift(sticker);
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(bootstrapQueryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(bootstrapQueryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: () => {
      void queryClient.refetchQueries(bootstrapQueryKey);
    },
  };

  return mutationOptions;
};

export default useCreateSticker;
